import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Agent } from '../types';
import { useAgentsApi } from '../apis/agents-apis';
import { 
  Search, 
  Plus, 
  Eye, 
  Trash2, 
  Loader2, 
  X, 
  Grid3X3,
  List,
  Settings,
  Tag,
  User,
  Star,
  Zap,
  Bot,
  ChevronDown,
  ChevronUp,
  Pencil
} from 'lucide-react';
import _ from 'lodash';
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { useToast } from "../components/ui/Toast";
import { useAuth } from '../contexts/AuthContext';
import { Badge } from "../components/ui/Badge";
import { getAgentIcon, getAgentCapabilityScore, checkEditPermission } from '../utils/agentUtils';

type ViewMode = 'grid' | 'list';
type FilterType = 'all' | 'owned' | 'shared' | 'core';
type SortType = 'name' | 'tools' | 'created' | 'updated';

export const AgentsListModern: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user, isAdmin } = useAuth();
  const agentsApi = useAgentsApi();
  const isMounted = useRef(false);

  // Data state
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [sortType, setSortType] = useState<SortType>('name');

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  const [expandedTags, setExpandedTags] = useState<Set<string>>(new Set());

  useEffect(() => {
    const controller = new AbortController();

    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const getAllAgents = false;
        const fetchedAgents = await agentsApi.getAgents(getAllAgents);

        if (isMounted.current) {
          setAgents(fetchedAgents);
        }
      } catch (error: unknown) {
        if (isMounted.current) {
          if (error instanceof Error && error.name !== 'AbortError') {
            console.error('Error fetching agents:', error);
            addToast({
              title: "Error",
              description: "Failed to retrieve agents. Please try again.",
              variant: "destructive",
            });
          }
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    isMounted.current = true;
    fetchInitialData();

    return () => {
      isMounted.current = false;
      controller.abort();
    };
  }, []);

  // Filter and sort agents
  const getFilteredAndSortedAgents = () => {
    let filtered = agents;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(agent =>
        agent.agentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.agentDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.agentTags?.some(tag => tag && typeof tag === 'string' && tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply type filter
    switch (filterType) {
      case 'owned':
        filtered = filtered.filter(agent => agent.ownerId === user?.sub);
        break;
      case 'shared':
        filtered = filtered.filter(agent => agent.ownerId !== user?.sub);
        break;
      case 'core':
        filtered = filtered.filter(agent => agent.isCore);
        break;
      default:
        break;
    }

    // Apply sorting
    switch (sortType) {
      case 'name':
        filtered = _.sortBy(filtered, 'agentName');
        break;
      case 'tools':
        filtered = _.sortBy(filtered, agent => agent.agentSelectedTools?.length || 0).reverse();
        break;
      case 'created':
        filtered = _.sortBy(filtered, 'createdAt').reverse();
        break;
      case 'updated':
        filtered = _.sortBy(filtered, 'updatedAt').reverse();
        break;
      default:
        break;
    }

    return filtered;
  };

  const filteredAgents = getFilteredAndSortedAgents();

  // Stats calculations
  const stats = useMemo(() => {
    const owned = agents.filter(agent => agent.ownerId === user?.sub).length;
    const shared = agents.filter(agent => agent.ownerId !== user?.sub).length;
    const core = agents.filter(agent => agent.isCore).length;
    
    return { total: agents.length, owned, shared, core };
  }, [agents, user?.sub]);

  const handleViewAgent = (agentId: string) => {
    navigate(`/studio/agents/view/${agentId}`);
  };

  const handleDeleteClick = (agent: Agent) => {
    setAgentToDelete(agent);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!agentToDelete?.agentId) return;

    try {
      setIsDeletingId(agentToDelete.agentId);
      await agentsApi.deleteAgent(agentToDelete.agentId);
      
      // Remove the agent from the state
      setAgents(prev => prev.filter(a => a.agentId !== agentToDelete.agentId));
      
      addToast({
        title: "Success",
        description: `Agent "${agentToDelete.agentName}" has been deleted.`,
        variant: "default",
      });
    } catch (error: unknown) {
      console.error('Error deleting agent:', error);
      addToast({
        title: "Error",
        description: "Failed to delete agent. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingId(null);
      setDeleteDialogOpen(false);
      setAgentToDelete(null);
    }
  };

  const toggleTags = (agentId: string) => {
    setExpandedTags(prev => {
      const newSet = new Set(prev);
      if (newSet.has(agentId)) {
        newSet.delete(agentId);
      } else {
        newSet.add(agentId);
      }
      return newSet;
    });
  };

  const renderAgentCard = (agent: Agent) => {
    const isOwned = agent.ownerId === user?.sub;
    const canEdit = checkEditPermission(agent, user?.sub || '', isAdmin || false);
    const canDelete = canEdit && !agent.isCore;
    const AgentIcon = getAgentIcon(agent);
    const capabilityScore = getAgentCapabilityScore(agent);
    const isTagsExpanded = expandedTags.has(agent.agentId || '');

    return (
              <Card key={agent.agentId} className="group hover:shadow-xl transition-all duration-300 border-purple-200 hover:border-purple-400 hover:scale-[1.02] relative ring-2 ring-transparent hover:ring-purple-200/50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            {/* Left side - Icon and Info */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {/* Agent Icon */}
              <div className="bg-gradient-to-br from-purple-100 to-indigo-200 p-3 rounded-xl flex-shrink-0 shadow-sm border border-purple-100">
                <AgentIcon className="h-6 w-6 text-purple-600" />
              </div>
              
              {/* Agent Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-purple-600 transition-colors truncate tracking-tight">
                    {agent.agentName}
                  </h3>
                  {isOwned && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-emerald-400 to-emerald-500 text-white shadow-sm flex-shrink-0">
                      <User className="w-3 h-3 mr-1" />
                      Owned
                    </span>
                  )}
                  {agent.isCore && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-purple-400 to-purple-500 text-white shadow-sm flex-shrink-0">
                      <Star className="w-3 h-3 mr-1" />
                      Core
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mb-2"
                   style={{
                     display: '-webkit-box',
                     WebkitLineClamp: 2,
                     WebkitBoxOrient: 'vertical' as const,
                     overflow: 'hidden'
                   }}>
                  {agent.agentDescription || 'No description provided.'}
                </p>
              </div>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleViewAgent(agent.agentId || '')}
                                    className="h-8 w-8 p-0 hover:bg-purple-50 hover:text-purple-600"
                title="View Agent"
              >
                <Eye className="h-4 w-4" />
              </Button>
              {canEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/studio/agents/edit/${agent.agentId}`)}
                  className="h-8 w-8 p-0 hover:bg-violet-50 hover:text-violet-600"
                  title="Edit Agent"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteClick(agent)}
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                  disabled={isDeletingId === agent.agentId}
                  title="Delete Agent"
                >
                  {isDeletingId === agent.agentId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Bottom section - Stats and Tags */}
          <div className="space-y-3">
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1 font-medium">
                <Settings className="w-3 h-3 text-blue-500" />
                {agent.agentSelectedTools?.length || 0} tools
              </span>
              <span className="flex items-center gap-1 font-medium">
                <Zap className="w-3 h-3 text-emerald-500" />
                {capabilityScore}% capable
              </span>
              {agent.agentTags && agent.agentTags.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleTags(agent.agentId || '')}
                  className="flex items-center gap-1 font-medium text-violet-600 hover:text-violet-700 hover:bg-violet-50 h-auto p-1 -ml-1"
                >
                  <Tag className="w-3 h-3" />
                  {agent.agentTags.length} tags
                  {isTagsExpanded ? (
                    <ChevronUp className="w-3 h-3" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                </Button>
              )}
            </div>

            {/* Expandable Tags Section */}
            {agent.agentTags && agent.agentTags.length > 0 && isTagsExpanded && (
              <div className="pt-2 border-t border-gray-100">
                <div className="flex flex-wrap gap-1">
                  {agent.agentTags.map((tag, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="text-xs bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderListItem = (agent: Agent) => {
    const isOwned = agent.ownerId === user?.sub;
    const canEdit = checkEditPermission(agent, user?.sub || '', isAdmin || false);
    const canDelete = canEdit && !agent.isCore;
    const AgentIcon = getAgentIcon(agent);

    return (
      <Card key={agent.agentId} className="mb-3 hover:shadow-lg transition-all duration-200 hover:border-blue-300 group">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {/* Agent Icon */}
              <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-2 rounded-lg flex-shrink-0">
                <AgentIcon className="h-5 w-5 text-blue-600" />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-gray-900 truncate tracking-tight">
                    {agent.agentName}
                  </h3>
                  <div className="flex items-center gap-2">
                    {isOwned && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-emerald-400 to-emerald-500 text-white shadow-sm">
                        <User className="w-3 h-3 mr-1" />
                        Owned
                      </span>
                    )}
                    {agent.isCore && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-purple-400 to-purple-500 text-white shadow-sm">
                        <Star className="w-3 h-3 mr-1" />
                        Core
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-1 truncate">
                  {agent.agentDescription || 'No description provided.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewAgent(agent.agentId || '')}
                className="border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300"
              >
                <Eye className="h-4 w-4 mr-1" />
                View
              </Button>
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/studio/agents/edit/${agent.agentId}`)}
                  className="border-violet-200 text-violet-600 hover:bg-violet-50 hover:border-violet-300"
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteClick(agent)}
                  className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                  disabled={isDeletingId === agent.agentId}
                >
                  {isDeletingId === agent.agentId ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Spinner className="w-8 h-8 text-blue-500" />
        <p className="text-gray-600">Loading agents...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl font-['Poppins','SF_Pro_Display','system-ui','-apple-system','sans-serif'] antialiased">
      {/* Hero Header Section */}
      <div className="relative mb-8 overflow-hidden">
        {/* Multi-layer gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/30 via-transparent to-purple-500/30"></div>
        
        {/* Animated background elements */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-400/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-blue-400/5 rounded-full blur-3xl animate-pulse delay-500"></div>
        
        {/* Glassmorphism overlay */}
        <div className="absolute inset-0 backdrop-blur-sm bg-gradient-to-r from-white/10 to-transparent"></div>
        
        {/* Content */}
        <div className="relative z-10 px-4 sm:px-8 py-8 sm:py-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <div className="bg-white/20 backdrop-blur-sm p-3 rounded-2xl shadow-lg self-start">
                  <Bot className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-2 drop-shadow-lg">
                    Intelligent Agents
                  </h1>
                  <p className="text-blue-100 text-base sm:text-lg font-medium max-w-2xl leading-relaxed">
                    Create, manage, and deploy powerful AI agents that automate complex workflows and enhance productivity across your organization
                  </p>
                </div>
              </div>
              
              {/* Live Stats */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-6">
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-white">{stats.total}</span> Total Agents
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-emerald-200">{stats.owned}</span> My Agents
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-purple-200">{stats.core}</span> Core Agents
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action Button */}
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/studio/agents/add')}
                className="group relative bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border border-white/30 hover:border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-semibold text-base sm:text-lg w-full sm:w-auto"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 p-1.5 sm:p-2 rounded-xl group-hover:bg-white/30 transition-colors">
                    <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <span>Create Agent</span>
                </div>
                
                {/* Subtle glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
              </Button>
            </div>
          </div>
        </div>
        

      </div>

      {/* Controls */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search agents"
              className="pl-10 pr-4 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as FilterType)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="all">All Agents</option>
            <option value="owned">My Agents</option>
            <option value="shared">Shared Agents</option>
            <option value="core">Core Agents</option>
          </select>

          <select
            value={sortType}
            onChange={(e) => setSortType(e.target.value as SortType)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="name">Sort by Name</option>
            <option value="tools">Sort by Tools</option>
            <option value="created">Sort by Created</option>
            <option value="updated">Sort by Updated</option>
          </select>

          <div className="flex items-center border border-gray-300 rounded-lg bg-white">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-blue-500'}`}
            >
              <Grid3X3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-blue-500'}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Results Info */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-600 font-medium">
          {searchTerm || filterType !== 'all' ? (
            <>Showing {filteredAgents.length} of {stats.total} agents</>
          ) : (
            <>Showing all {stats.total} agents</>
          )}
        </p>
      </div>

      {/* Agent Grid/List */}
      {filteredAgents.length === 0 ? (
        <Card className="p-12 text-center border-2 border-dashed border-gray-300">
          <div className="flex flex-col items-center gap-4">
            <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-4 rounded-full">
              <Bot className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 tracking-tight font-['Radley','serif']">
                {searchTerm ? 'No agents found' : 'No agents yet'}
              </h3>
              <p className="text-gray-600 mb-4 max-w-md">
                {searchTerm 
                  ? 'Try adjusting your search or filters to find what you\'re looking for.'
                  : 'Get started by creating your first agent to automate tasks and workflows.'
                }
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => navigate('/studio/agents/add')}
                  className="group relative bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-8 py-4 rounded-xl font-semibold text-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2 rounded-lg group-hover:bg-white/30 transition-colors">
                      <Plus className="h-5 w-5" />
                    </div>
                    <span>Create Your First Agent</span>
                  </div>
                  
                  {/* Subtle glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-500 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 -z-10 blur-xl"></div>
                </Button>
              )}
            </div>
          </div>
        </Card>
      ) : (
        <div className={
          viewMode === 'grid' 
            ? 'grid gap-4 grid-cols-1 lg:grid-cols-2' 
            : 'space-y-3'
        }>
          {filteredAgents.map(agent => 
            viewMode === 'grid' ? renderAgentCard(agent) : renderListItem(agent)
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirmed}
        title="Delete Agent"
        description="Are you sure you want to delete this agent? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        requireVerification={true}
        verificationWord="DELETE"
      />
    </div>
  );
};

export default AgentsListModern; 